import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ShieldCheck, MapPin, Sparkles, Zap, KeyRound, Clock,
  BadgeCheck, Wallet, LifeBuoy, HeartHandshake, Rocket, PhoneCall,
  CheckCircle2, Compass, Search, CarFront, FileCheck2, Play, TrendingUp,
  Star, Briefcase, Timer, Layers,
} from "lucide-react";
import { VehicleCard } from "@/components/vehicle/VehicleCard";
import { VehicleCardSkeleton } from "@/components/vehicle/VehicleCardSkeleton";

// ---------- helpers ----------
function isTransparent(url) {
  return typeof url === "string" && (url.includes("curbo.s3.amazonaws.com") || url.includes("_remove_bg") || url.includes("/brand/alfadrive-lion-transparent"));
}

// ---------- Spotlight (rotating featured vehicle) ----------
const HeroSpotlight = ({ vehicles }) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!vehicles?.length) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % vehicles.length), 5500);
    return () => clearInterval(t);
  }, [vehicles]);

  const v = vehicles?.[index];
  if (!v) {
    return (
      <div className="rounded-[36px] border border-border bg-white p-6 h-[420px] animate-pulse w-full max-w-full" />
    );
  }
  const transparent = isTransparent(v.image_url);
  return (
    <div className="relative rounded-[28px] sm:rounded-[36px] border border-border bg-white overflow-hidden w-full max-w-full">
      {/* Header row */}
      <div className="flex items-center justify-between px-4 sm:px-6 pt-4 sm:pt-6 gap-2 flex-wrap">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-[10px] uppercase tracking-widest text-muted-foreground">
          <Sparkles className="h-3 w-3 text-[hsl(var(--primary))]" /> Spotlight Vehicle
        </div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground text-right">Explore premium access</div>
      </div>

      {/* Image area — flush on the card background, no inner border/frame.
          Fixed aspect ratio keeps card height identical across vehicles. */}
      <div
        className="relative mt-2 sm:mt-3 mx-3 sm:mx-4 overflow-hidden"
        style={{ aspectRatio: "16 / 10" }}
      >
        <div className="absolute inset-0">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={v.id}
              initial={{ x: "100%", opacity: 0.85 }}
              animate={{ x: "0%", opacity: 1 }}
              exit={{ x: "-100%", opacity: 0.85 }}
              transition={{ duration: 0.65, ease: [0.22, 0.61, 0.36, 1] }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <img
                src={v.image_url}
                alt={v.name}
                className="max-h-full max-w-[92%] object-contain"
                onError={(e) => { e.currentTarget.src = "/brand/alfadrive-lion-transparent.png"; e.currentTarget.style.opacity = "0.5"; }}
              />
            </motion.div>
          </AnimatePresence>
        </div>
        {transparent && <div aria-hidden className="absolute inset-x-14 bottom-4 h-4 rounded-full blur-lg bg-[hsl(var(--charcoal-900))]/12 pointer-events-none" />}
      </div>

      {/* Details — fixed min-height to prevent card height jumping between vehicles */}
      <div className="p-4 sm:p-6 pt-4 sm:pt-5 min-h-[130px]">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">{v.brand}</div>
            <div className="mt-1 font-display text-lg sm:text-2xl font-semibold truncate">{v.name}</div>
            <div className="mt-1 text-[11px] sm:text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 fill-[hsl(var(--gold-stamp))] text-[hsl(var(--gold-stamp))]" /> {v.rating?.toFixed?.(1) || "4.9"}</span>
              <span>·</span>
              <span>{v.seats} seats</span>
              <span>·</span>
              <span className="truncate">{v.fuel_type}</span>
            </div>
          </div>
          <Button asChild size="sm" className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))] shrink-0 h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm" data-testid="spotlight-reserve-btn">
            <Link to={`/vehicles/${v.id}`}>Reserve <ArrowRight className="ml-1 h-3.5 w-3.5 sm:h-4 sm:w-4" /></Link>
          </Button>
        </div>
        {/* dots */}
        <div className="mt-4 sm:mt-5 flex items-center gap-1.5">
          {vehicles.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Spotlight vehicle ${i + 1}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${i === index ? "w-6 bg-[hsl(var(--primary))]" : "w-1.5 bg-border hover:bg-[hsl(var(--charcoal-300))]"}`}
              data-testid={`spotlight-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------- Brand marquee ----------
const BrandStrip = ({ brands }) => {
  if (!brands?.length) return null;
  const doubled = [...brands, ...brands];
  return (
    <section className="border-y border-border bg-white" data-testid="home-brand-strip">
      <div className="container-x py-6 sm:py-8">
        <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
          <div className="h-px flex-1 bg-border" />
          <span>Vehicles by trusted brands</span>
          <div className="h-px flex-1 bg-border" />
        </div>
      </div>
      <div className="marquee pb-6 sm:pb-8">
        <div className="marquee-track slow items-center">
          {doubled.map((b, i) => (
            <div
              key={`${b.id}-${i}`}
              className="mx-6 sm:mx-9 opacity-75 hover:opacity-100 transition-opacity flex items-center"
              style={{ height: 80 }}
            >
              <img
                src={b.logo_url}
                alt={b.name}
                className="max-h-14 sm:max-h-16 max-w-[220px] sm:max-w-[240px] object-contain"
                loading="lazy"
                onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("span"), { className: "text-2xl sm:text-3xl font-display font-semibold text-muted-foreground whitespace-nowrap", textContent: b.name })); }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------- Section: Value cards ----------
const VALUE_CARDS = [
  { icon: ShieldCheck, t: "Verified drivers. Trusted access.", d: "Every rental is supported by secure identity verification to help protect our customers, vehicles, and rental experience." },
  { icon: MapPin,      t: "Access vehicles across Ontario.",  d: "From major cities to growing communities, AlfaDrive helps keep you moving throughout Ontario." },
  { icon: Wallet,      t: "Transparent pricing from the start.", d: "Clear pricing, visible deposits, and no hidden surprises at checkout." },
  { icon: Clock,       t: "Reserve anytime, drive with confidence.", d: "Book vehicles through the platform any time, day or night, with support available when you need it." },
  { icon: Sparkles,    t: "Premium vehicles, flexible terms.", d: "Choose short-term rentals, monthly plans, or longer-term access that fits your needs." },
  { icon: LifeBuoy,    t: "Support that stays with you.",     d: "Our team is available 24/7/365 to help guide bookings, answer questions, and support your rental experience." },
];

const STEPS = [
  { icon: Search,      t: "Browse",  d: "Explore premium vehicles across sedans, SUVs, EVs, trucks, and more." },
  { icon: CalIconLike, t: "Reserve", d: "Choose your dates, term, or plan and lock in your vehicle quickly." },
  { icon: FileCheck2,  t: "Verify",  d: "Complete secure identity verification and upload required documents online." },
  { icon: CarFront,    t: "Drive",   d: "Pick up or access your vehicle with AlfaDrive support available whenever you need it." },
];

function CalIconLike(props) { return <BadgeCheck {...props} />; }

// ---------- Main ----------
export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const [vRes, bRes] = await Promise.all([
          axios.get(`${API}/vehicles/featured?limit=8`),
          axios.get(`${API}/brands`),
        ]);
        setFeatured(vRes.data || []);
        setBrands(bRes.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const spotlightPool = useMemo(() => {
    const list = [...featured].sort((a, b) => (isTransparent(a.image_url) ? -1 : 0) - (isTransparent(b.image_url) ? -1 : 0));
    return list.slice(0, Math.min(5, list.length || 5));
  }, [featured]);

  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* ============================ HERO ============================ */}
      <section className="relative border-b border-border" data-testid="home-hero">
        <div className="absolute inset-0 -z-10 hero-wash" />
        <div aria-hidden className="pointer-events-none absolute -top-24 -right-32 h-[520px] w-[520px] rounded-full glow-blue -z-10" />
        <div aria-hidden className="pointer-events-none absolute bottom-0 -left-24 h-[380px] w-[380px] rounded-full glow-blue -z-10" />
        <div className="container-x py-14 sm:py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
              className="lg:col-span-7 min-w-0"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-[11px] uppercase tracking-widest text-muted-foreground shadow-sm">
                <Zap className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Modern mobility platform · Ontario
              </div>
              <h1 className="mt-5 font-display text-5xl sm:text-7xl font-bold leading-[1.02] tracking-tight" data-testid="hero-headline">
                Flexible vehicle rentals. <span className="text-[hsl(var(--primary))]">Done properly.</span>
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed" data-testid="hero-subheadline">
                With premium vehicle access across Ontario, transparent pricing, and flexible terms, it’s easier than ever to drive what you want, when you want. Wherever you’re going next, AlfaDrive has you covered.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild className="h-12 px-6 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))] shadow-[0_10px_30px_-12px_hsla(220,90%,45%,0.55)]" data-testid="hero-browse-btn">
                  <Link to="/browse">Browse vehicles <ArrowRight className="ml-1 h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="h-12 px-5 rounded-xl border-border" data-testid="hero-how-btn">
                  <Link to="/about">How it works</Link>
                </Button>
                {!user?.is_member && (
                  <Button asChild variant="ghost" className="h-12 px-5 rounded-xl text-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-100))]">
                    <Link to="/drive-alfa-club">Drive Alfa Club <ChevronRightIcon /></Link>
                  </Button>
                )}
              </div>

              {/* Trust chips */}
              <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
                <div className="inline-flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                  </span>
                  Support online · 24/7/365
                </div>
                <div className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Ontario-wide</div>
                <div className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Insured &amp; verified</div>
                <div className="inline-flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" /> Transparent pricing</div>
              </div>
            </motion.div>

            {/* Spotlight vehicle */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.12 }}
              className="lg:col-span-5 min-w-0"
              data-testid="hero-spotlight"
            >
              <HeroSpotlight vehicles={spotlightPool} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============================ BRAND CAROUSEL ============================ */}
      <BrandStrip brands={brands} />

      {/* ============================ POPULAR THIS WEEK ============================ */}
      <section className="section-y bg-[hsl(var(--charcoal-100))]" data-testid="home-popular">
        <div className="container-x">
          <div className="flex items-end justify-between gap-6 mb-8">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Popular this week</div>
              <h2 className="mt-2 font-display text-3xl sm:text-5xl font-semibold leading-tight">Trending across our fleet</h2>
              <p className="mt-2 text-muted-foreground max-w-xl hidden sm:block">Hand-picked vehicles reserved most this week — reserve before they’re gone.</p>
            </div>
            <Button asChild variant="ghost" className="text-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-100))] whitespace-nowrap">
              <Link to="/browse">See all <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <VehicleCardSkeleton key={i} />)
              : featured.slice(0, 6).map((v) => <VehicleCard key={v.id} vehicle={v} />)}
          </div>
        </div>
      </section>

      {/* ============================ VALUE CARDS ============================ */}
      <section className="section-y bg-white" data-testid="home-values">
        <div className="container-x">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Why AlfaDrive</div>
            <h2 className="mt-2 font-display text-3xl sm:text-5xl font-semibold leading-tight">Premium access, real support, honest pricing.</h2>
            <p className="mt-3 text-muted-foreground">Everything we build is designed around one goal — making it easier for you to drive the right vehicle, on your terms.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {VALUE_CARDS.map((c, i) => (
              <motion.div
                key={c.t}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-3xl bg-white border border-border p-6 shine-on-hover hover:border-[hsl(var(--blue-300))] transition-colors"
                data-testid={`value-card-${i}`}
              >
                <div className="h-11 w-11 rounded-2xl bg-[hsl(var(--blue-100))] text-[hsl(var(--primary))] flex items-center justify-center">
                  <c.icon className="h-5 w-5" />
                </div>
                <div className="mt-4 font-display text-xl font-semibold leading-snug">{c.t}</div>
                <div className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.d}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ HOW IT WORKS ============================ */}
      <section className="section-y bg-[hsl(var(--charcoal-100))]" data-testid="home-how">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">How AlfaDrive works</div>
            <h2 className="mt-2 font-display text-3xl sm:text-5xl font-semibold leading-tight">Reserve in minutes. Drive with confidence.</h2>
            <p className="mt-3 text-muted-foreground">Four simple steps — browse the fleet, book your term, verify your identity online, and drive.</p>
          </div>
          <div className="mt-12 relative">
            {/* connector line on lg */}
            <div aria-hidden className="hidden lg:block absolute left-0 right-0 top-[46px] h-px bg-gradient-to-r from-transparent via-border to-transparent" />
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {STEPS.map((s, i) => (
                <motion.div
                  key={s.t}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="relative rounded-3xl bg-white border border-border p-6 shine-on-hover"
                  data-testid={`how-step-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-2xl bg-[hsl(var(--charcoal-900))] text-white flex items-center justify-center relative">
                      <s.icon className="h-5 w-5" />
                    </div>
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Step {i + 1}</div>
                  </div>
                  <div className="mt-4 font-display text-xl font-semibold">{s.t}</div>
                  <div className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.d}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================ CLUB + ONE PATH TEASERS ============================ */}
      <section className="section-y bg-white" data-testid="home-teasers">
        <div className="container-x grid lg:grid-cols-2 gap-5">
          {/* Drive Alfa Club */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="relative rounded-[32px] p-8 sm:p-10 overflow-hidden bg-gradient-to-br from-[hsl(var(--charcoal-900))] to-[hsl(var(--blue-700))] text-white shadow-[0_30px_80px_-40px_hsla(220,90%,25%,0.45)]"
            data-testid="teaser-club"
          >
            <div aria-hidden className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsla(220,100%,70%,0.28),transparent_55%)]" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 backdrop-blur px-3 py-1 text-[11px] uppercase tracking-widest">
                <KeyRound className="h-3.5 w-3.5 text-[hsl(var(--blue-300))]" /> Drive Alfa Club
              </div>
              <h3 className="mt-5 font-display text-3xl sm:text-4xl font-semibold leading-tight">Unlock member pricing &amp; premium access.</h3>
              <p className="mt-3 text-white/85 max-w-md">Member pricing, flexible vehicle access, Rental Term Leasing options, and premium benefits through AlfaDrive’s consumer membership experience.</p>
              <ul className="mt-5 grid grid-cols-2 gap-2 text-sm">
                {["Member pricing","RTL access","Priority booking","Perks &amp; savings"].map((p) => (
                  <li key={p} className="inline-flex items-center gap-2 text-white/90"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--blue-300))]" /> <span dangerouslySetInnerHTML={{ __html: p }} /></li>
                ))}
              </ul>
              <div className="mt-8">
                <Button asChild className="h-12 px-6 rounded-xl bg-white text-[hsl(var(--charcoal-900))] hover:bg-[hsl(var(--blue-100))]" data-testid="teaser-club-cta">
                  <Link to="/drive-alfa-club">Explore Drive Alfa Club <ArrowRight className="ml-1 h-4 w-4" /></Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Alfa One Path */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative rounded-[32px] p-8 sm:p-10 overflow-hidden bg-white border border-border shadow-[0_30px_80px_-40px_hsla(220,90%,25%,0.2)]"
            data-testid="teaser-onepath"
          >
            <div aria-hidden className="absolute -top-24 -right-24 h-64 w-64 rounded-full glow-blue" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-[11px] uppercase tracking-widest text-muted-foreground">
                <Briefcase className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Alfa One Path · Business mobility
              </div>
              <h3 className="mt-5 font-display text-3xl sm:text-4xl font-semibold leading-tight">Business vehicles that keep work moving.</h3>
              <p className="mt-3 text-muted-foreground max-w-md">A business mobility program built for operators, contractors, rideshare drivers, and companies that need reliable access to vehicles that keep work moving.</p>
              <ul className="mt-5 grid grid-cols-2 gap-2 text-sm">
                {[
                  { i: Briefcase, t: "Contractors" },
                  { i: TrendingUp, t: "Rideshare / delivery" },
                  { i: Layers,    t: "3 · 6 · 12-month terms" },
                  { i: Rocket,    t: "Priority access" },
                ].map((p) => (
                  <li key={p.t} className="inline-flex items-center gap-2 text-foreground/90"><p.i className="h-4 w-4 text-[hsl(var(--primary))]" /> {p.t}</li>
                ))}
              </ul>
              <div className="mt-8">
                <Button asChild className="h-12 px-6 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]" data-testid="teaser-onepath-cta">
                  <Link to="/alfa-one-path">Explore Alfa One Path <ArrowRight className="ml-1 h-4 w-4" /></Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================ 24/7/365 ACCESS ============================ */}
      <section className="section-y bg-[hsl(var(--charcoal-900))] text-white relative overflow-hidden" data-testid="home-support">
        <div aria-hidden className="absolute inset-0 -z-0 bg-[radial-gradient(circle_at_20%_15%,hsla(220,90%,55%,0.22),transparent_50%),radial-gradient(circle_at_80%_80%,hsla(220,100%,60%,0.14),transparent_55%)]" />
        <div className="relative container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="text-[11px] uppercase tracking-widest text-white/60">24/7/365 access &amp; support</div>
              <h2 className="mt-2 font-display text-3xl sm:text-5xl font-semibold leading-tight">Access when you need it. <span className="text-[hsl(var(--blue-300))]">Support when it matters.</span></h2>
              <p className="mt-5 text-white/85 text-lg max-w-2xl leading-relaxed">
                AlfaDrive gives customers a modern way to access vehicles around the clock. Whether it’s an early morning reservation, a late-night booking, or help during your rental, our platform and 24/7/365 support team help keep you moving.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild className="h-12 px-6 rounded-xl bg-white text-[hsl(var(--charcoal-900))] hover:bg-[hsl(var(--blue-100))]" data-testid="support-browse-btn"><Link to="/browse">Browse vehicles <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
                <span className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white/10 text-xs text-white/85 backdrop-blur"><PhoneCall className="h-3.5 w-3.5" /> Talk to our team anytime</span>
              </div>
            </div>
            <div className="lg:col-span-5 grid grid-cols-2 gap-3">
              {[
                { i: Clock,        t: "Book any hour",   d: "Reserve early morning, late night — whenever business or life needs it." },
                { i: HeartHandshake,t: "Real people",     d: "Speak with real humans on our support desk, day or night." },
                { i: Timer,        t: "Fast response",   d: "Quick help when travel plans or business timelines change." },
                { i: Compass,      t: "Digital-first",   d: "Book, extend, and manage everything through our modern platform." },
              ].map((c) => (
                <div key={c.t} className="rounded-2xl border border-white/15 bg-white/5 backdrop-blur p-5 hover:border-white/30 hover:bg-white/10 transition-colors">
                  <c.i className="h-5 w-5 text-[hsl(var(--blue-300))]" />
                  <div className="mt-3 font-display text-base font-semibold">{c.t}</div>
                  <div className="mt-1 text-xs text-white/75">{c.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================ TRUST STRIP ============================ */}
      <section className="section-y bg-white" data-testid="home-trust">
        <div className="container-x">
          <div className="grid md:grid-cols-3 gap-4 sm:gap-5">
            {[
              { i: ShieldCheck, t: "Insured &amp; verified fleet",     d: "Every rental is protected by our insurance program and secure driver verification." },
              { i: Wallet,      t: "Transparent pricing, always",      d: "Governing rates, weekly rates, monthly deals, and deposits — visible before you book." },
              { i: Sparkles,    t: "Short-term &amp; long-term access", d: "Hourly, daily, weekly, monthly and 3 · 6 · 12-month Rental Term Leasing options." },
            ].map((c) => (
              <div key={c.t} className="rounded-3xl border border-border bg-gradient-to-br from-white to-[hsl(var(--charcoal-100))] p-6 shine-on-hover">
                <c.i className="h-6 w-6 text-[hsl(var(--primary))]" />
                <div className="mt-3 font-display text-lg font-semibold" dangerouslySetInnerHTML={{ __html: c.t }} />
                <div className="mt-1 text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: c.d }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ FINAL CTA ============================ */}
      <section className="section-y bg-white" data-testid="home-final-cta">
        <div className="container-x">
          <div className="rounded-[32px] bg-gradient-to-br from-[hsl(var(--charcoal-900))] to-[hsl(var(--blue-700))] p-10 sm:p-14 text-white relative overflow-hidden">
            <div aria-hidden className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsla(220,100%,70%,0.28),transparent_50%)]" />
            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <div className="text-[11px] uppercase tracking-widest text-white/70">Ready when you are</div>
                <h2 className="mt-2 font-display text-3xl sm:text-5xl font-semibold leading-tight">Book the drive. <span className="text-[hsl(var(--blue-300))]">Own the moment.</span></h2>
                <p className="mt-4 text-white/85 max-w-2xl">From short-term escapes to long-term business mobility, AlfaDrive gives you a beautifully simple way to reserve premium vehicles across Ontario.</p>
              </div>
              <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
                <Button asChild className="h-12 px-6 rounded-xl bg-white text-[hsl(var(--charcoal-900))] hover:bg-[hsl(var(--blue-100))]" data-testid="cta-browse"><Link to="/browse">Browse vehicles</Link></Button>
                <Button asChild variant="outline" className="h-12 px-5 rounded-xl border-white/40 text-white bg-transparent hover:bg-white/10" data-testid="cta-how"><Link to="/about">How it works</Link></Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ChevronRightIcon() {
  return <ArrowRight className="ml-1 h-4 w-4" />;
}
